import { ReactElement, useMemo } from 'react';
import { Paper, Skeleton, Typography } from '@mui/material';
import { Address } from 'viem';

import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { DECIMAL_PLACES_SHORT, HUNDRED, ZERO } from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { formatThousandAmount } from 'modules/common/utils/formatThousandAmount';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetPoolSharesQuery } from 'modules/ownerPanel/actions/getPoolShares';
import { useGetAccountPoolQuery } from 'modules/pool/actions/getAccountPool';
import { useGetPoolQuery } from 'modules/pool/actions/getPool';
import { useGetPoolOwnerQuery } from 'modules/pool/actions/getPoolOwner';
import { useGetPoolAPYs } from 'modules/pool/hooks/useGetPoolAPYs';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface IReturnStatsProps {
  poolAddress: Address;
}

export function ReturnStats({ poolAddress }: IReturnStatsProps): ReactElement {
  const { classes, cx } = useStyles();
  const { t, keys } = useGlobalTranslation(translation);
  const { connectionKey } = useConnection();

  const tokenPerYear = t(keys.unit.tokenPerYear, {
    token: t(keys.tokens.main),
  });

  const { data: pool } = useGetPoolQuery({ address: poolAddress });
  const { data: poolShares, isLoading: isPoolSharesLoading } =
    useGetPoolSharesQuery();

  const { data: ownerAddress = '' } = useGetPoolOwnerQuery({
    address: poolAddress,
  });

  const { data: accountOwnerPool } = useGetAccountPoolQuery(
    {
      connectionKey,
      address: poolAddress,
      accountAddress: ownerAddress as Address,
    },
    { skip: !ownerAddress, selectFromResult: mapDataToUndefinedIfSkip },
  );

  const delegationsAmount = useMemo(
    () => pool?.tvl?.minus(accountOwnerPool?.stakedAmount ?? ZERO),
    [accountOwnerPool?.stakedAmount, pool?.tvl],
  );

  const { poolAPYs, isLoading: isAPYLoading } = useGetPoolAPYs();
  const APY = poolAPYs[poolAddress];

  const delegationAPY = useMemo(() => {
    if (!pool || !APY) {
      return undefined;
    }

    return APY.multipliedBy(pool.commission).dividedBy(HUNDRED);
  }, [pool, APY]);

  const incomeAmount = useMemo(() => {
    if (
      !poolShares ||
      !delegationsAmount ||
      !pool ||
      !accountOwnerPool ||
      !APY ||
      !delegationAPY
    ) {
      return undefined;
    }

    return poolShares
      .plus(accountOwnerPool.stakedAmount.multipliedBy(APY.div(HUNDRED)))
      .plus(delegationsAmount.multipliedBy(delegationAPY.div(HUNDRED)));
  }, [
    APY,
    accountOwnerPool,
    delegationAPY,
    delegationsAmount,
    pool,
    poolShares,
  ]);

  const incomeAmountUSD = useGetUSDAmount(incomeAmount);

  return (
    <Paper className={classes.root}>
      <Typography textTransform="uppercase" variant="h5">
        {t(keys.title)}
      </Typography>

      <div className={classes.stats}>
        <div className={cx(classes.stat, classes.filledStat)}>
          <Typography
            color={theme => theme.palette.text.secondary}
            fontWeight={600}
            variant="body1"
          >
            {t(keys.income)}
          </Typography>

          <Typography component="div" variant="h4">
            {incomeAmount ? (
              t(keys.unit.tokenValue, {
                value: formatThousandAmount(incomeAmount),
                token: tokenPerYear,
              })
            ) : (
              <Skeleton width={100} />
            )}
          </Typography>

          {incomeAmountUSD && (
            <Typography
              color={theme => theme.palette.text.secondary}
              variant="body1"
            >
              {t(keys.unit.$, {
                value: incomeAmountUSD
                  .decimalPlaces(DECIMAL_PLACES_SHORT)
                  .toFormat(),
              })}
            </Typography>
          )}
        </div>

        <div className={classes.stat}>
          <Typography
            color={theme => theme.palette.text.secondary}
            fontWeight={600}
            variant="body1"
          >
            {t(keys.shares)}
          </Typography>

          <Typography component="div" variant="h5">
            {isPoolSharesLoading || !poolShares ? (
              <Skeleton width={100} />
            ) : (
              t(keys.unit.tokenValue, {
                value: formatThousandAmount(poolShares),
                token: tokenPerYear,
              })
            )}
          </Typography>
        </div>

        <div className={classes.stat}>
          <Typography
            color={theme => theme.palette.text.secondary}
            fontWeight={600}
            variant="body1"
          >
            {t(keys.stakeAPY)}
          </Typography>

          <Typography component="div" variant="h5">
            {!APY || isAPYLoading ? (
              <Skeleton width={100} />
            ) : (
              t(keys.unit.percent, {
                value: APY.decimalPlaces(2).toFormat(),
              })
            )}
          </Typography>
        </div>

        <div className={classes.stat}>
          <Typography
            color={theme => theme.palette.text.secondary}
            fontWeight={600}
            variant="body1"
          >
            {t(keys.commissionAPY)}
          </Typography>

          <Typography component="div" variant="h5">
            {delegationAPY ? (
              t(keys.unit.percent, {
                value: delegationAPY.decimalPlaces(2).toFormat(),
              })
            ) : (
              <Skeleton width={100} />
            )}
          </Typography>
        </div>
      </div>
    </Paper>
  );
}
